import React, { useEffect, useState } from "react";
import DataTable from 'react-data-table-component-with-filter';
import { useNavigate } from 'react-router-dom';
import { FaThList, FaTh, FaSearch, FaArchive, FaCaretDown, FaCog, FaUpload } from 'react-icons/fa';
import '../Patients/Patients.css';
import InternalHeader from '../InternalHeader';
import Sidebar from '../Sidebar';
import KanbanView from '../Patients/KanbanView';
import CustomPagination from '../CustomPagination/CustomPagination';
import Filter from "../Filter/Filter";
import Action from "../Actions/Actions";
import AccessControl from '../AccessControl';

const Dentists = () => {
    const [dentists, setDentists] = useState([]);
    const [isSidebarVisible, setIsSidebarVisible] = useState(true);
    const [searchText, setSearchText] = useState("");
    const [isKanbanView, setIsKanbanView] = useState(false);
    const [viewType, setViewType] = useState('table');
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [viewOption, setViewOption] = useState('all');
    const [selectedDentists, setSelectedDentists] = useState([]);
    const [isActionsDropdownOpen, setIsActionsDropdownOpen] = useState(false);
    const [logs, setLogs] = useState([]);

    const logChange = (action, details) => {
        setLogs(prevLogs => [...prevLogs, { timestamp: new Date().toLocaleString(), action, details }]);
    };

    const handleExport = () => {
        // Export logic here
    };

    const totalRecords = dentists.length;

    const toggleSidebar = () => {
        setIsSidebarVisible(!isSidebarVisible);
    };

    useEffect(() => {
        const fetchDentists = async () => {
            try {
                const response = await fetch('https://api.hgcdental.clinic/api/dentists');
                const json = await response.json();
                if (response.ok) {
                    setDentists(json);
                    logChange('Fetched', `Loaded ${json.length} dentists.`);
                }
            } catch (error) {
                console.error("Error fetching dentists:", error);
            }
        };
        fetchDentists();
    }, []);

    const handleRowsPerPageChange = (newRowsPerPage) => {
        setRowsPerPage(Number(newRowsPerPage));
        setCurrentPage(1);
    };

    const filteredData = dentists
        .filter(dentist =>
            `${dentist.firstName} ${dentist.middleName} ${dentist.lastName}`.toLowerCase().includes(searchText.toLowerCase())
        )
        .filter(dentist => {
            if (viewOption === 'archived') {
                return dentist.isArchived === true;
            }
            return dentist.isArchived !== true;
        });

    const handleSearchClear = () => {
        setSearchText("");
        setViewOption("all");
    };

    const paginatedData = filteredData.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    const totalPages = Math.ceil(filteredData.length / rowsPerPage);

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const handleRowClick = (row) => {
        navigate(`/dentist-profile/${row._id}`);
    };

    const handleDeleteSelected = async () => {
        if (selectedDentists.length === 0) {
            alert('No dentists selected for archiving!');
            return;
        }

        const confirmArchive = window.confirm('Are you sure you want to archive the selected dentists?');
        if (!confirmArchive) {
            return;
        }

        try {
            const response = await fetch('https://api.hgcdental.clinic/api/dentists/archive', {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    dentistIds: selectedDentists.map(dentist => dentist._id)
                })
            });

            const result = await response.json();

            if (response.ok) {
                setDentists(prevDentists =>
                    prevDentists.map(dentist =>
                        selectedDentists.some(selected => selected._id === dentist._id)
                            ? { ...dentist, isArchived: true }
                            : dentist
                    )
                );
                setSelectedDentists([]);
                alert(result.message);
            } else {
                alert(result.message || 'Failed to archive dentists');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('An error occurred while archiving dentists');
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const year = date.getFullYear().toString().slice(-2);

        return `${month < 10 ? '0' + month : month}/${day < 10 ? '0' + day : day}/${year}`;
    };

    const handleActionsToggle = () => {
        setIsActionsDropdownOpen(!isActionsDropdownOpen);
    };

    const columns = [
        { name: 'Dentist ID', selector: row => row.dentistID, sortable: true },
        {
            name: 'Name',
            selector: row => `${row.firstName} ${row.middleName} ${row.lastName}`.trim(), // Full Name
            sortable: true
        },
        { name: 'Email', selector: row => row.email, sortable: true },
    ];

    const handleRowSelected = ({ selectedRows }) => {
        console.log("Selected Rows:", selectedRows); // Debugging log
        setSelectedDentists(selectedRows);
    };

    const customStyles = {
        rows: {
            style: {
                borderBottom: '1px solid #ddd',
            },
        },
        headCells: {
            style: {
                backgroundColor: '#007bff',
                color: 'white',
                fontWeight: 'bold',
                padding: '15px 10px',
                textAlign: 'center',
            },
        },
        cells: {
            style: {
                padding: '12px 10px',
                borderRight: '1px solid #ddd',
            },
        },
        pagination: {
            style: {
                display: 'flex',
                justifyContent: 'center',
                padding: '10px',
                backgroundColor: '#f4f4f4',
            },
        },
        sortIcon: {
            style: {
                color: '#007bff',
                fontSize: '18px',
            },
        },
    };

    return (
        <AccessControl>
            <div className={`data-table-page ${isSidebarVisible ? 'sidebar-visible' : 'sidebar-hidden'}`}>
                <InternalHeader moduleName="Dentists" toggleSidebar={toggleSidebar} />
                <div className="data-table-container">
                    <Sidebar isSidebarVisible={isSidebarVisible} />
                    <div className="data-table-container2">
                        <div className="data-table-header">
                            <div className="flex-div">
                                <a href="/create-dentist">
                                    <button className="create-button">New</button>
                                </a>
                            </div>
                            <div>
                                {selectedDentists.length > 0 ? (
                                    <div className="actions-dropdown-container">
                                        <button onClick={handleActionsToggle} className="actions-button">
                                            <FaCog />  Actions
                                        </button>
                                        {isActionsDropdownOpen && (
                                            <div className="actions-dropdown-options1">
                                                <div onClick={handleExport}>
                                                    <FaUpload /> Export Records
                                                </div>
                                                <div onClick={handleDeleteSelected}>
                                                    <FaArchive /> Archive
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <Filter
                                        searchText={searchText}
                                        setSearchText={setSearchText}
                                        viewOption={viewOption}
                                        setViewOption={setViewOption}
                                        onClear={handleSearchClear}
                                    />
                                )}
                            </div>
                            <div className="column-div">
                                {!isKanbanView && (
                                    <CustomPagination
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        rowsPerPage={rowsPerPage}
                                        onPageChange={handlePageChange}
                                        onRowsPerPageChange={handleRowsPerPageChange}
                                        totalRecords={filteredData.length}
                                        onRowClicked={handleRowClick}
                                    />
                                )}
                                <button
                                    onClick={() => {
                                        const newKanbanView = !isKanbanView;
                                        setIsKanbanView(newKanbanView);
                                        setViewType(newKanbanView ? 'kanban' : 'table');
                                    }}
                                    className="view-toggle-button"
                                >
                                    {viewType === 'table' ? <FaTh size={20} /> : <FaThList size={20} />}
                                </button>
                            </div>
                        </div>
                        <div>
                            {isKanbanView ? (
                                <div className="table-form-kanban">
                                    <KanbanView patients={filteredData} onRowClick={handleRowClick} />
                                </div>
                            ) : (
                                <div className="table-form">
                                    <div>
                                        <DataTable
                                            title="Dentists"
                                            columns={columns}
                                            data={paginatedData}
                                            highlightOnHover
                                            selectableRows
                                            selectableRowsHighlight
                                            onRowClicked={handleRowClick}
                                            onSelectedRowsChange={handleRowSelected}
                                            striped
                                            customStyles={customStyles}
                                        />
                                    </div>
                                </div>
                            )}
                            <div className="log-display-section">
                                <h2>Logs</h2>
                                <div className="log-container">
                                    <ul>
                                        {logs.map((log, index) => (
                                            <li key={index}>{log.timestamp} - {log.action}: {log.details}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </AccessControl>
    );
};

export default Dentists;